import {
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { myFetch } from '@/commons/fetch';
import { useUser } from '@/contexts/UserContext';
import { useAppDispatch } from '@store';
import { addFileToFolder } from '@store/slices/foldersSlice';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

interface AddFileModalProps {
  opened: boolean;
  onClose: () => void;
  folderCategory: string;
  folderName: string;
  folderFiles: any[];
}

export const AddFileModal = ({
  opened,
  onClose,
  folderCategory,
  folderName,
  folderFiles,
}: AddFileModalProps) => {
  const intl = useIntl();
  const { user } = useUser();
  const dispatch = useAppDispatch();
  const [models, setModels] = useState<any[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!opened || !user?.username) return;

    const fetchModels = async () => {
      try {
        setIsLoadingModels(true);
        const response = await myFetch(`/${user.username}/models`);

        // Check if folder has a CONTRACT file
        const hasContract = folderFiles.some(
          (file) => file.type === 'CONTRACT'
        );

        // Filter models by category
        // If no CONTRACT exists, allow both CONTRACT and ANNEX
        // If CONTRACT exists, only allow ANNEX
        const filteredModels = response.data.filter((model: any) => {
          if (model.category !== folderCategory) {
            return false;
          }
          if (hasContract) {
            return model.type === 'ANNEX';
          }
          return true; // Allow both CONTRACT and ANNEX when no CONTRACT exists
        });

        setModels(filteredModels);
      } catch (error: any) {
        notifications.show({
          color: 'red',
          title: intl.formatMessage({ id: 'error' }),
          message: error.message,
        });
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModels();
  }, [opened, user?.username, folderCategory, folderFiles, intl]);

  const handleClose = () => {
    setSelectedModelId(null);
    setFileName('');
    onClose();
  };

  const handleModelSelect = (modelId: string | null) => {
    setSelectedModelId(modelId);

    if (modelId) {
      const selectedModel = models.find((m) => m.id === modelId);
      if (selectedModel) {
        setFileName(selectedModel.name);
      }
    } else {
      setFileName('');
    }
  };

  const handleCreate = async () => {
    if (!selectedModelId) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'pleaseSelectAModel' }),
      });
      return;
    }

    if (!fileName.trim()) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'fileNameRequired' }),
      });
      return;
    }

    try {
      setIsCreating(true);

      // Find selected model
      const selectedModel = models.find((m) => m.id === selectedModelId);

      if (!selectedModel) {
        throw new Error('Model not found');
      }

      // Create file data with only name, fields, and template
      const fileData = {
        name: fileName.trim(),
        type: selectedModel.type, // Use model's type (CONTRACT or ANNEX)
        fields: selectedModel.fields || [],
        template: selectedModel.template || '',
      };

      await dispatch(
        addFileToFolder({
          folderName,
          fileData,
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'fileCreated' },
          { fileName: fileName.trim() }
        ),
      });

      handleClose();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message
          ? `${fileName.trim()}: ${error.message}`
          : intl.formatMessage(
              { id: 'actionFailed' },
              { name: fileName.trim() }
            ),
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'addFile' })}
      centered
    >
      {isLoadingModels ? (
        <Center h={200}>
          <Loader />
        </Center>
      ) : (
        <Stack gap="md">
          {models.length === 0 ? (
            <Text size="sm" c="dimmed">
              {intl.formatMessage({ id: 'noModelsAvailable' })}
            </Text>
          ) : (
            <>
              <Select
                label={intl.formatMessage({ id: 'selectModel' })}
                placeholder={`${intl.formatMessage({ id: 'select' })}...`}
                data={models.map((model) => ({
                  value: model.id,
                  label: model.name,
                }))}
                value={selectedModelId}
                onChange={handleModelSelect}
                searchable
                required
              />

              <TextInput
                label={intl.formatMessage({ id: 'name' })}
                placeholder={intl.formatMessage({ id: 'useAnotherName' })}
                value={fileName}
                onChange={(e) => setFileName(e.currentTarget.value)}
                required
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleClose}>
                  {intl.formatMessage({ id: 'cancel' })}
                </Button>
                <Button
                  onClick={handleCreate}
                  loading={isCreating}
                  disabled={!selectedModelId || !fileName.trim()}
                >
                  {intl.formatMessage({ id: 'create' })}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
};
